import type { FriendsOverview, PlayerId, RoomPlayerInfo } from '@pixelworld/shared';
import { useState } from 'react';
import { da } from '../../i18n/da';
import { askToBeFriends, inviteFriend } from './friends-api';
import styles from './RoomFriendControls.module.css';

/** The friends list as the room sees it, and what to do when it changes. */
export interface RoomFriendsList {
  overview: FriendsOverview | null;
  reload: () => void;
}

type AskState = 'asking' | 'asked' | 'tooManyFriends' | 'tooManyRequests' | 'failed';

/** "Bliv venner" next to a player in the room, or what is already between the two. */
export function BefriendButton({
  player,
  list,
}: {
  player: RoomPlayerInfo;
  list: RoomFriendsList;
}) {
  const [state, setState] = useState<AskState | null>(null);
  const { overview } = list;
  if (!overview) return null;
  if (overview.friends.some((friend) => friend.id === player.id)) {
    return <span className={styles.tag}>{da.friends.isFriend}</span>;
  }
  if (state === 'asked' || overview.outgoing.includes(player.id)) {
    return <span className={styles.tag}>{da.friends.asked}</span>;
  }
  if (state === 'tooManyFriends' || state === 'tooManyRequests' || state === 'failed') {
    return (
      <span className={styles.note} role="status">
        {da.friends.askErrors[state]}
      </span>
    );
  }

  const ask = async () => {
    setState('asking');
    const result = await askToBeFriends(player.id);
    if (!result.ok) return setState('failed');
    if (!result.data.ok) {
      return setState(
        result.data.error === 'TOO_MANY_FRIENDS'
          ? 'tooManyFriends'
          : result.data.error === 'TOO_MANY_REQUESTS'
            ? 'tooManyRequests'
            : 'failed',
      );
    }
    setState('asked');
    list.reload();
  };

  return (
    <button
      type="button"
      className={styles.befriend}
      disabled={state === 'asking'}
      onClick={() => void ask()}
    >
      {da.friends.befriend}
    </button>
  );
}

/** Friends who are online but not in this room, with "Kom og spil". */
export function FriendsElsewhere({
  players,
  list,
}: {
  players: RoomPlayerInfo[];
  list: RoomFriendsList;
}) {
  const [called, setCalled] = useState<Map<PlayerId, 'sent' | 'tooSoon' | 'failed'>>(new Map());
  const here = new Set(players.map((player) => player.id));
  const online = (list.overview?.friends ?? []).filter(
    (friend) => friend.where.status !== 'offline' && !here.has(friend.id),
  );
  if (online.length === 0) return null;

  const call = async (playerId: PlayerId) => {
    const result = await inviteFriend(playerId);
    const outcome = !result.ok
      ? 'failed'
      : result.data.ok
        ? 'sent'
        : result.data.error === 'TOO_SOON'
          ? 'tooSoon'
          : 'failed';
    setCalled((current) => new Map(current).set(playerId, outcome));
  };

  return (
    <li className={styles.elsewhere}>
      <span className={styles.heading}>{da.friends.elsewhere}</span>
      <ul className={styles.friends}>
        {online.map((friend) => {
          const outcome = called.get(friend.id);
          return (
            <li key={friend.id} className={styles.friend}>
              <span className={styles.name}>{friend.displayName}</span>
              {outcome === 'sent' || outcome === 'tooSoon' || outcome === 'failed' ? (
                <span className={styles.note} role="status">
                  {da.friends.callOutcome[outcome]}
                </span>
              ) : (
                <button
                  type="button"
                  className={styles.befriend}
                  onClick={() => void call(friend.id)}
                >
                  {da.friends.callOver}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </li>
  );
}
